import {NavLink} from 'react-router-dom';
import {
    Alert,
    Code,
    Group,
    Paper,
    Stack,
    Table,
    Text,
    Tooltip,
} from '@mantine/core';

import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';
import {
    OutcomeBadge,
    TestObservation,
} from '../../test-observations';

// FNP-904 — the evidence behind a signature: which users, which wakes, the full
// error text. This is what lets a human judge whether a defect is real BEFORE
// filing it, rather than trusting the grouping blindly.
export const ContributingObservations = ({
                                             observations,
                                             occurrenceCount,
                                             loading,
                                         }: {
    observations: TestObservation[],
    occurrenceCount: number,
    loading?: boolean,
}) => {

    return (
        <Paper withBorder p={'md'} radius={'md'}>
            <Stack gap={'sm'}>
                <Group justify={'space-between'} align={'baseline'}>
                    <Text size={'sm'} fw={500}>Contributing observations</Text>
                    <Text size={'xs'} c={'dimmed'}>
                        {/*
                          States BOTH numbers rather than implying the list is
                          complete. An observation does not store its fingerprint
                          (it is derived by deployed code, deliberately), so these
                          are matched within the loaded observation window — a
                          defect older than that window shows fewer rows than its
                          lifetime count. Saying "showing N of M" is the honest
                          version; showing N alone would read as the whole truth.
                        */}
                        {loading
                            ? 'Loading…'
                            : `showing ${observations.length} of ${occurrenceCount} recorded`}
                    </Text>
                </Group>

                {!loading && observations.length === 0 && (
                    <Alert color={'gray'} title={'No matching observations loaded'}>
                        The observations that produced this signature are older than the
                        loaded window, or have since been removed. The counts above come from
                        the signature row itself and remain accurate.
                    </Alert>
                )}

                {observations.length > 0 && (
                    <Table verticalSpacing={'xs'} highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Observed</Table.Th>
                                <Table.Th>Synthetic user</Table.Th>
                                <Table.Th>Outcome</Table.Th>
                                <Table.Th>Error</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {observations.map((observation) => (
                                <Table.Tr key={observation.id}>
                                    <Table.Td>
                                        <NavLink to={`/test-observations/${encodeURIComponent(observation.id)}`}>
                                            <Text size={'xs'}>{formatTimestamp(observation.observedAt)}</Text>
                                        </NavLink>
                                    </Table.Td>
                                    <Table.Td>
                                        <NavLink to={`/synthetic-users/${encodeURIComponent(observation.syntheticUserId)}`}>
                                            <Text size={'xs'} ff={'monospace'}>
                                                {observation.syntheticUserId}
                                            </Text>
                                        </NavLink>
                                    </Table.Td>
                                    <Table.Td>
                                        <OutcomeBadge record={observation}/>
                                    </Table.Td>
                                    <Table.Td>
                                        {observation.error
                                            ? (
                                                <Tooltip label={observation.error} multiline w={420} withArrow>
                                                    <Code style={{fontSize: '0.7rem'}}>
                                                        {observation.error.slice(0, 80)}
                                                        {observation.error.length > 80 ? '…' : ''}
                                                    </Code>
                                                </Tooltip>
                                            )
                                            : <Text size={'xs'} c={'dimmed'}>—</Text>}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </Stack>
        </Paper>
    );
};
